"use client";

import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from "@heroui/dropdown";
import {
  BookBookmark02Icon,
  BookOpen02Icon,
  BubbleChatQuestionIcon,
  CircleArrowUp02Icon,
  CloudDownloadIcon,
  CustomerService01Icon,
  GitPullRequestIcon,
  KeyboardIcon,
  Layers01Icon,
  Logout02Icon,
  MapsIcon,
  PackageOpenIcon,
  QuillWrite01Icon,
  Settings01Icon,
} from "@icons";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { type ReactNode, useState } from "react";
import { useKeyboardShortcuts } from "@/components/providers/KeyboardShortcutsProvider";
import {
  type ConfirmAction,
  ConfirmActionDialog,
} from "@/components/shared/ConfirmActionDialog";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { ChevronRight, Github } from "@/components/shared/icons";
import { getLinkByLabel } from "@/config/appConfig";
import { useUserSubscriptionStatus } from "@/features/pricing/hooks/usePricing";
import ContactSupportModal from "@/features/support/components/ContactSupportModal";
import { WhatsNewTimelineMenu } from "@/features/whats-new/components/WhatsNewTimelineMenu";
import { useReleases } from "@/features/whats-new/hooks/useReleases";
import {
  type PlatformInfo,
  usePlatformDetection,
} from "@/hooks/ui/usePlatformDetection";
import { useConfirmation } from "@/hooks/useConfirmation";
import { usePricingModalStore } from "@/stores/pricingModalStore";
import { settingsPageItems, socialMediaItems } from "../config/settingsConfig";
import { useNestedMenu } from "../hooks/useNestedMenu";
import { NestedMenuTooltip } from "./NestedMenuTooltip";

export type ModalAction = "clear_chats" | "logout";

interface MenuItem {
  key: string;
  label: string;
  icon?: React.ComponentType<{ className?: string; color?: string }>;
  href?: string;
  action?: () => void;
  color?: "danger" | "default";
  external?: boolean;
  hasSubmenu?: boolean;
  iconColor?: string;
  badge?: React.ReactNode;
  customClassNames?: {
    title?: string;
  };
}

export default function SettingsMenu({
  children,
  onOpenChange,
}: {
  children?: ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
}) {
  const router = useRouter();
  const { confirmationProps } = useConfirmation();

  const discordLink = getLinkByLabel("Discord");
  const whatsappLink = getLinkByLabel("WhatsApp");
  const docsLink = getLinkByLabel("Documentation");
  const githubLink = getLinkByLabel("GitHub");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportModalType, setSupportModalType] = useState<
    string | undefined
  >();
  const [modalAction, setModalAction] = useState<ModalAction | null>(null);
  const { data: subscriptionStatus } = useUserSubscriptionStatus();
  const openPricingModal = usePricingModalStore((s) => s.openModal);
  const { unseen: unseenReleases } = useReleases();

  const whatsNewMenu = useNestedMenu();
  const resourcesMenu = useNestedMenu();
  const supportMenu = useNestedMenu();
  const downloadMenu = useNestedMenu();

  // Maps a submenu item's key to the nested-menu controller driving its flyout.
  const submenuByKey: Record<string, ReturnType<typeof useNestedMenu>> = {
    "whats-new": whatsNewMenu,
    download: downloadMenu,
    resources: resourcesMenu,
    support: supportMenu,
  };

  const { currentPlatform, desktopPlatforms } = usePlatformDetection();
  const { openShortcutsModal } = useKeyboardShortcuts();

  const iconClasses = "w-[18px] h-[18px]";

  const resourcesMenuItems = [
    {
      key: "documentation",
      label: "Documentation",
      icon: BookBookmark02Icon,
      action: () => window.open(docsLink?.href, "_blank"),
    },
    {
      key: "release-notes",
      label: "Release Notes",
      icon: Layers01Icon,
      action: () =>
        window.open("https://docs.heygaia.io/release-notes", "_blank"),
    },
    {
      key: "blog",
      label: "Blog",
      icon: QuillWrite01Icon,
      action: () => router.push("/blog"),
    },
    {
      key: "roadmap",
      label: "Roadmap",
      icon: MapsIcon,
      action: () =>
        window.open("https://gaia.featurebase.app/roadmap", "_blank"),
    },
    {
      key: "opensource",
      label: "Open Source",
      icon: Github,
      action: () => window.open(githubLink?.href, "_blank"),
    },
  ];

  const supportMenuItems = [
    {
      key: "contact_support",
      label: "Contact Support",
      icon: BubbleChatQuestionIcon,
      action: () => {
        setSupportModalType("support");
        setSupportModalOpen(true);
      },
    },
    {
      key: "feature_request",
      label: "Request a Feature",
      icon: GitPullRequestIcon,
      action: () => {
        setSupportModalType("feature");
        setSupportModalOpen(true);
      },
    },
  ];

  // Sort platforms with current platform first
  const sortedPlatforms = [
    ...desktopPlatforms.filter((p) => p.platform === currentPlatform.platform),
    ...desktopPlatforms.filter((p) => p.platform !== currentPlatform.platform),
  ];

  const downloadMenuItems = sortedPlatforms.map((platform: PlatformInfo) => ({
    key: platform.platform,
    label: platform.shortName,
    iconElement: (
      <div className="relative h-[18px] w-[18px]">
        <Image
          src={platform.iconPath}
          alt={platform.displayName}
          fill
          className="object-contain"
        />
      </div>
    ),
    action: () => {
      if (platform.downloadUrl) {
        window.open(platform.downloadUrl, "_blank");
      } else {
        router.push("/download");
      }
    },
  }));

  const socialMediaColorMap: Record<string, string> = {
    discord: "#5865F2",
    whatsapp: "#25d366",
  };

  const handleItemAction = (item: MenuItem) => {
    if (item.href) {
      router.push(item.href);
    } else if (item.action) {
      item.action();
    } else if (item.external) {
      const linkMap: Record<string, string | undefined> = {
        discord: discordLink?.href,
        whatsapp: whatsappLink?.href,
        documentation: docsLink?.href,
      };
      const url = linkMap[item.key];
      if (url) window.open(url, "_blank");
    }
  };

  const menuSections = [
    ...(subscriptionStatus?.is_subscribed
      ? []
      : [
          {
            title: undefined,
            showDivider: true,
            items: [
              {
                key: "upgrade_to_pro",
                label: "Upgrade to Pro",
                action: openPricingModal,
                icon: CircleArrowUp02Icon,
                iconColor: "#00bbff",
                customClassNames: { title: "text-primary font-medium" },
              },
            ],
          },
        ]),
    {
      title: undefined,
      showDivider: true,
      items: [
        ...settingsPageItems.filter((item) =>
          ["memory", "linked-accounts", "usage"].includes(item.key),
        ),
        {
          key: "keyboard_shortcuts",
          label: "Keyboard Shortcuts",
          icon: KeyboardIcon,
          action: openShortcutsModal,
        },
      ],
    },
    {
      title: "Community",
      showDivider: true,
      items: socialMediaItems,
    },
    {
      title: undefined,
      showDivider: false,
      items: [
        {
          key: "whats-new",
          label: "What's new",
          icon: PackageOpenIcon,
          hasSubmenu: true,
          badge:
            unseenReleases.length > 0 ? (
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            ) : undefined,
        },
        {
          key: "download",
          label: `Download for ${currentPlatform.isMobile ? "Desktop" : currentPlatform.shortName.split(" ")[0]}`,
          icon: CloudDownloadIcon,
          hasSubmenu: true,
        },
        {
          key: "resources",
          label: "Resources",
          icon: BookOpen02Icon,
          hasSubmenu: true,
        },
        {
          key: "support",
          label: "Support",
          icon: CustomerService01Icon,
          hasSubmenu: true,
        },
        {
          key: "settings",
          label: "Settings",
          icon: Settings01Icon,
          action: () => router.push("/settings"),
        },
        {
          key: "logout",
          label: "Sign Out",
          icon: Logout02Icon,
          color: "danger" as const,
          action: () => setModalAction("logout"),
        },
      ],
    },
  ];

  return (
    <>
      <Dropdown
        placement="right"
        className="bg-secondary-bg text-foreground dark shadow-xl"
        offset={21}
        isOpen={isMenuOpen}
        onOpenChange={(open) => {
          setIsMenuOpen(open);
          onOpenChange?.(open);
        }}
      >
        <DropdownTrigger>{children}</DropdownTrigger>
        <DropdownMenu aria-label="Settings Menu" variant="faded">
          {menuSections.map((section, index) => (
            <DropdownSection
              key={section.title || `section-${index}`}
              title={section.title}
              showDivider={section.showDivider}
              classNames={{ divider: "bg-zinc-800/60" }}
            >
              {section.items.map((item: MenuItem) => {
                const Icon = item.icon;
                const iconColor =
                  item.iconColor || socialMediaColorMap[item.key];

                // Handle nested menus (What's new, Download, Resources, Support)
                if (item.hasSubmenu) {
                  const menu = submenuByKey[item.key] ?? supportMenu;

                  return (
                    <DropdownItem
                      key={item.key}
                      textValue={item.label}
                      variant="flat"
                      onMouseEnter={menu.handleMouseEnter}
                      onMouseLeave={menu.handleMouseLeave}
                      className="text-zinc-400 transition hover:text-white"
                      startContent={Icon && <Icon className={iconClasses} />}
                      endContent={
                        <div className="flex items-center gap-1.5">
                          {item.badge}
                          <ChevronRight className="h-4 w-4 text-zinc-500" />
                        </div>
                      }
                    >
                      {item.label}
                    </DropdownItem>
                  );
                }

                return (
                  <DropdownItem
                    key={item.key}
                    textValue={item.label}
                    variant="flat"
                    color={item.color}
                    onPress={() => handleItemAction(item)}
                    className={
                      item.color === "danger"
                        ? "text-danger"
                        : iconColor
                          ? "transition"
                          : "text-zinc-400 transition hover:text-white"
                    }
                    style={iconColor ? { color: iconColor } : undefined}
                    startContent={
                      Icon && <Icon className={iconClasses} color={iconColor} />
                    }
                    classNames={item.customClassNames}
                  >
                    {item.label}
                  </DropdownItem>
                );
              })}
            </DropdownSection>
          ))}
        </DropdownMenu>
      </Dropdown>

      <NestedMenuTooltip
        isOpen={whatsNewMenu.isOpen}
        onOpenChange={whatsNewMenu.setIsOpen}
        itemRef={whatsNewMenu.itemRef}
        customContent={
          <WhatsNewTimelineMenu
            onClose={() => {
              whatsNewMenu.setIsOpen(false);
              setIsMenuOpen(false);
              onOpenChange?.(false);
            }}
          />
        }
      />

      <NestedMenuTooltip
        isOpen={resourcesMenu.isOpen}
        onOpenChange={resourcesMenu.setIsOpen}
        itemRef={resourcesMenu.itemRef}
        menuItems={resourcesMenuItems}
        iconClasses={iconClasses}
      />

      <NestedMenuTooltip
        isOpen={supportMenu.isOpen}
        onOpenChange={supportMenu.setIsOpen}
        itemRef={supportMenu.itemRef}
        menuItems={supportMenuItems}
        iconClasses={iconClasses}
      />

      <NestedMenuTooltip
        isOpen={downloadMenu.isOpen}
        onOpenChange={downloadMenu.setIsOpen}
        itemRef={downloadMenu.itemRef}
        menuItems={downloadMenuItems}
        iconClasses={iconClasses}
      />

      <ContactSupportModal
        isOpen={supportModalOpen}
        onOpenChange={() => {
          setSupportModalOpen(false);
          setSupportModalType(undefined);
        }}
        initialValues={
          supportModalType ? { type: supportModalType } : undefined
        }
      />

      <ConfirmActionDialog
        action={modalAction as ConfirmAction}
        onOpenChange={(action) => setModalAction(action as ModalAction)}
      />

      <ConfirmationDialog {...confirmationProps} />
    </>
  );
}
